import * as M from "@radix-ui/react-dropdown-menu";
import clsx from "clsx";
import { Check, ChevronRight } from "./icons";
import type { ComponentProps, ReactNode } from "react";
import s from "./Menu.module.css";

export const Menu = M.Root;
export const MenuTrigger = M.Trigger;
export const MenuRadioGroup = M.RadioGroup;

export function MenuContent({ className, align = "end", ...rest }: ComponentProps<typeof M.Content>) {
  return (
    <M.Portal>
      <M.Content className={clsx(s.content, className)} align={align} sideOffset={6} collisionPadding={8} {...rest} />
    </M.Portal>
  );
}

interface ItemExtras {
  icon?: ReactNode;
  /** A key or chord, shown as keycaps ("Shift+U", "#"). */
  shortcut?: string;
  /** Quiet text at the end of the row, like a time. */
  detail?: string;
  danger?: boolean;
}

export function MenuItem({ icon, shortcut, detail, danger, className, children, ...rest }: ComponentProps<typeof M.Item> & ItemExtras) {
  return (
    <M.Item className={clsx(s.item, danger && s.danger, className)} aria-keyshortcuts={shortcut} {...rest}>
      {icon && <span className={s.icon}>{icon}</span>}
      <span className={s.text}>{children}</span>
      {detail && <span className={s.detail}>{detail}</span>}
      {shortcut && (
        <span className={s.shortcut} aria-hidden>
          {shortcut.split("+").map((k) => (
            <kbd key={k} className={s.key}>{k}</kbd>
          ))}
        </span>
      )}
    </M.Item>
  );
}

export function MenuRadioItem({ icon, className, children, ...rest }: ComponentProps<typeof M.RadioItem> & Pick<ItemExtras, "icon">) {
  return (
    <M.RadioItem className={clsx(s.item, className)} {...rest}>
      {icon && <span className={s.icon}>{icon}</span>}
      <span className={s.text}>{children}</span>
      <M.ItemIndicator className={s.indicator}>
        <Check />
      </M.ItemIndicator>
    </M.RadioItem>
  );
}

export function MenuCheckboxItem({ icon, className, children, ...rest }: ComponentProps<typeof M.CheckboxItem> & Pick<ItemExtras, "icon">) {
  return (
    <M.CheckboxItem className={clsx(s.item, s.checkItem, className)} {...rest}>
      <span className={s.box} aria-hidden>
        <M.ItemIndicator className={s.boxMark}>{rest.checked === "indeterminate" ? <span className={s.dash} /> : <Check />}</M.ItemIndicator>
      </span>
      {icon && <span className={s.icon}>{icon}</span>}
      <span className={s.text}>{children}</span>
    </M.CheckboxItem>
  );
}

export const MenuSub = M.Sub;

export function MenuSubTrigger({ icon, className, children, ...rest }: ComponentProps<typeof M.SubTrigger> & Pick<ItemExtras, "icon">) {
  return (
    <M.SubTrigger className={clsx(s.item, className)} {...rest}>
      {icon && <span className={s.icon}>{icon}</span>}
      <span className={s.text}>{children}</span>
      <span className={s.chevron} aria-hidden>
        <ChevronRight />
      </span>
    </M.SubTrigger>
  );
}

export function MenuSubContent({ className, ...rest }: ComponentProps<typeof M.SubContent>) {
  return (
    <M.Portal>
      <M.SubContent className={clsx(s.content, className)} sideOffset={4} collisionPadding={8} {...rest} />
    </M.Portal>
  );
}

export function MenuLabel({ className, ...rest }: ComponentProps<typeof M.Label>) {
  return <M.Label className={clsx(s.label, className)} {...rest} />;
}

export function MenuSeparator() {
  return <M.Separator className={s.separator} />;
}
